/**
 * Мелкие элементы форм кабинета: поле ввода, строка «подпись — значение», строка-переход,
 * пояснение под блоком.
 *
 * Живут здесь, а не в `src/ui/`: там общий набор для обеих ролей, а это формы кабинета,
 * которых у клиента нет вовсе. Внешний вид взят из прототипа (`.inp`, `.setrow`, `.hintbox`).
 *
 * Цвета — только токены темы (`bg-surface`, `text-ink-3`, …), то есть в конечном счёте
 * `var(--tg-theme-*)`. Литерал вроде `bg-white` пережил бы переключение клиента в тёмную
 * тему и оставил бы белый прямоугольник поверх тёмного фона (ТЗ §14.3).
 */

import type { ReactNode } from 'react';

/** Пояснение под блоком — прототипный `.hintbox` / `.alert-b`. */
export function Hint(props: { children: ReactNode }): JSX.Element {
  return (
    <div className="mt-2.5 rounded-[11px] bg-surface-2 px-3 py-2.5 text-[12px] leading-[1.45] text-ink-3">
      {props.children}
    </div>
  );
}

/**
 * Поле ввода с подписью, пояснением и текстом ошибки.
 *
 * Ошибка рисуется под полем и не заменяет собой пояснение: владелец, промахнувшийся
 * значением, должен видеть одновременно и что ввёл не то, и что вообще значит это поле.
 */
export function Field(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  note?: string | undefined;
  error?: string | undefined;
  /** `numeric` поднимает цифровую клавиатуру телефона, но НЕ фильтрует ввод: разбор — в `rules.ts`. */
  numeric?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="mb-3.5 block">
      <div className="mb-1.5 text-[12.5px] font-semibold text-ink-2">{props.label}</div>
      <input
        type="text"
        inputMode={props.numeric ? 'numeric' : 'text'}
        value={props.value}
        placeholder={props.placeholder}
        disabled={props.disabled ?? false}
        onChange={(event) => props.onChange(event.target.value)}
        className={`w-full rounded-[12px] bg-surface px-3.5 py-3 text-[14px] text-ink outline-none placeholder:text-ink-3 disabled:text-ink-3 ${
          props.error
            ? 'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--red)_55%,transparent)]'
            : 'shadow-[inset_0_0_0_1px_var(--hair)]'
        }`}
      />
      {props.error ? (
        <div className="mt-1.5 text-[11.5px] font-semibold leading-[1.4] text-danger">
          {props.error}
        </div>
      ) : null}
      {props.note ? (
        <div className="mt-1.5 text-[11.5px] leading-[1.45] text-ink-3">{props.note}</div>
      ) : null}
    </label>
  );
}

/** Строка «подпись — значение» только на чтение. Прототипный `.setrow`. */
export function ValueRow(props: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-hair py-3 last:border-b-0">
      <div className="min-w-0 text-[13.5px]">{props.label}</div>
      <div className="shrink-0 text-[13px] font-semibold text-ink-3">{props.value}</div>
    </div>
  );
}

/** Строка-переход на вложенный экран. Шеврон справа — как в прототипе (`.go`). */
export function NavRow(props: { label: string; hint?: string | undefined; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex w-full items-center justify-between gap-3 border-b border-hair py-3.5 text-left last:border-b-0"
    >
      <div className="min-w-0">
        <div className="text-[14px] font-semibold">{props.label}</div>
        {props.hint ? (
          <div className="mt-0.5 text-[12px] leading-[1.4] text-ink-3">{props.hint}</div>
        ) : null}
      </div>
      <span className="shrink-0 text-[18px] leading-none text-ink-3">›</span>
    </button>
  );
}

/** Карточка-обёртка блока настроек. */
export function Card(props: { children: ReactNode }): JSX.Element {
  return (
    <div className="rounded-card bg-surface px-3.5 py-1 shadow-[inset_0_0_0_1px_var(--hair)]">
      {props.children}
    </div>
  );
}
